import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import { Section, Container } from "@/components/shared/Section";
import { GlassPanel } from "@/components/shared/GlassPanel";
import { SignalChip } from "@/components/shared/SignalChip";
import { AnimatedReveal } from "@/components/shared/AnimatedReveal";
import { SEO } from "@/components/shared/SEO";
import { searchMetaAds } from "@/lib/api";
import { Search, ArrowRight, ExternalLink, Eye } from "lucide-react";

const EXAMPLES = ["nike.com", "allbirds", "gymshark", "glossier"];

const COUNTRY_OPTIONS = [
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "RO", label: "Romania" },
  { code: "AU", label: "Australia" },
  { code: "CA", label: "Canada" },
  { code: "NL", label: "Netherlands" },
  { code: "IT", label: "Italy" },
  { code: "ES", label: "Spain" },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
}

function truncate(text, max = 220) {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

export default function MetaAdsSearchPage() {
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("US");
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ads, setAds] = useState([]);

  const runSearch = useCallback(
    async (overrideTerms) => {
      const terms = (overrideTerms ?? query).trim();
      if (!terms) return;
      setQuery(terms);
      setSearched(true);
      setLoading(true);
      setError(null);
      setAds([]);
      try {
        const payload = await searchMetaAds({
          searchTerms: terms,
          countryCode: country,
          status: "ACTIVE",
          limit: 20,
        });
        setAds(Array.isArray(payload?.data) ? payload.data : []);
      } catch (err) {
        const apiMsg = err.response?.data?.message;
        const apiErr = err.response?.data?.error;
        setError(apiMsg || apiErr || err.message || "Search failed. Please try again.");
        setAds([]);
      } finally {
        setLoading(false);
      }
    },
    [query, country],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    void runSearch();
  };

  return (
    <MarketingLayout>
      <SEO
        title="Free Meta Ads Search"
        description="Search the Meta Ad Library for any brand or competitor. See active Facebook and Instagram ads instantly."
        canonical="/tools/meta-ads-search"
      />
      <Section hero className="relative overflow-hidden">
        <div className="absolute inset-0 radar-glow-hero opacity-40" />
        <Container className="relative z-10">
          <AnimatedReveal>
            <div className="mx-auto max-w-2xl text-center">
              <SignalChip variant="lime" className="mb-4">
                Free Tool
              </SignalChip>
              <h1 className="mb-4 text-3xl font-display font-bold tracking-tight text-foreground sm:text-4xl">
                Free Meta Ads Search
              </h1>
              <p className="mb-8 text-base text-muted-foreground">
                Search the Meta Ad Library for any brand or competitor. See active Facebook and Instagram ads instantly.
              </p>

              <form onSubmit={handleSubmit} className="relative text-left" data-testid="tools-meta-search-form">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Country</label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-foreground outline-none focus:border-lime-500/30 sm:max-w-[220px]"
                    aria-label="Ad library country"
                  >
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-2 focus-within:border-lime-500/30">
                  <Search className="ml-2 h-5 w-5 shrink-0 text-muted-foreground" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g. Nike, Sephora, your niche…"
                    className="flex-1 bg-transparent py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground"
                    data-testid="tools-meta-ads-search-input"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="rounded-lg bg-[#CCFF6C] px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-[#D9FF9A] disabled:opacity-60"
                    data-testid="tools-meta-search-button"
                  >
                    {loading ? "Searching…" : "Search"}
                  </button>
                </div>
              </form>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className="text-xs text-muted-foreground">Try:</span>
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    disabled={loading}
                    onClick={() => void runSearch(ex)}
                    className="rounded-md border border-white/[0.06] bg-white/[0.03] px-2 py-1 text-xs text-foreground/60 transition-colors hover:text-lime-400 disabled:opacity-50"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </AnimatedReveal>
        </Container>
      </Section>

      <Section dense>
        <Container>
          {!searched ? (
            <AnimatedReveal>
              <GlassPanel className="p-12 text-center">
                <Search className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
                <h3 className="mb-2 text-lg font-display font-semibold text-foreground">Search for a brand to get started</h3>
                <p className="mx-auto max-w-md text-sm text-muted-foreground">
                  Enter a brand or keyword above to see recent active ads in the country you pick.
                </p>
              </GlassPanel>
            </AnimatedReveal>
          ) : (
            <AnimatedReveal>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {loading ? (
                    <>Searching Meta Ad Library…</>
                  ) : error ? (
                    <span className="text-rose-400">{error}</span>
                  ) : (
                    <>
                      Showing <span className="font-medium text-foreground">{ads.length}</span> ads for{" "}
                      <span className="font-medium text-foreground">"{query}"</span>
                      {country ? ` in ${country}` : ""}
                    </>
                  )}
                </p>

                {!loading && !error && ads.length === 0 ? (
                  <GlassPanel className="p-8 text-center">
                    <p className="text-sm text-muted-foreground">No active ads matched this search. Try different keywords or another country.</p>
                  </GlassPanel>
                ) : null}

                {ads.map((ad) => (
                  <GlassPanel key={ad.id} hover className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:gap-6">
                    <div className="flex h-16 w-full shrink-0 items-center justify-center rounded-lg border border-white/[0.06] bg-white/[0.04] sm:h-16 sm:w-24">
                      <Eye className="h-6 w-6 text-muted-foreground/40" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{ad.page_name || "Unknown page"}</p>
                        <SignalChip className="text-[9px]">Meta</SignalChip>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Started {formatDate(ad.ad_delivery_start_time)}
                        {ad.ad_delivery_stop_time ? ` · Ended ${formatDate(ad.ad_delivery_stop_time)}` : ""}
                      </p>
                      {ad.ad_creative_body ? (
                        <p className="mt-2 text-sm leading-relaxed text-foreground/80">{truncate(ad.ad_creative_body)}</p>
                      ) : null}
                    </div>
                    {ad.ad_snapshot_url ? (
                      <a
                        href={ad.ad_snapshot_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex shrink-0 items-center gap-1.5 self-start text-xs font-medium text-lime-400 hover:text-lime-300 sm:self-center"
                        aria-label="View ad in Meta Ad Library"
                      >
                        View <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : null}
                  </GlassPanel>
                ))}

                <GlassPanel className="border-lime-500/10 p-8 text-center">
                  <p className="mb-2 text-sm text-foreground">Want more? This is just a preview.</p>
                  <p className="mb-6 text-xs text-muted-foreground">
                    Get real-time monitoring, alerts, and analytics with Radar.
                  </p>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 rounded-xl bg-[#CCFF6C] px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#D9FF9A]"
                  >
                    Start free trial <ArrowRight className="h-4 w-4" />
                  </Link>
                </GlassPanel>
              </div>
            </AnimatedReveal>
          )}
        </Container>
      </Section>
    </MarketingLayout>
  );
}
